import type Address from "@services/sdk/models/address";
import type AddressSuggestion from "@services/sdk/models/address-suggestion";
import type Asset from "@services/sdk/models/asset";
import type AuthResponse from "@services/sdk/models/auth-response";
import type Campaign from "@services/sdk/models/campaign";
import type ErrorResponse from "@services/sdk/models/error-response";
import type LegalContent from "@services/sdk/models/legal-content";
import type { MarketingPreferences } from "@services/sdk/models/marketing-preferences";
import type PlanSummary from "@services/sdk/models/plan-summary";
import type Product from "@services/sdk/models/product";
import type WelcomeCarouselSlide from "@services/sdk/models/welcome-carousel-slide";
import type { Moment } from "moment";

interface MapFns<T, E, TResult, EResult> {
	success: (a: T) => TResult;
	failure: (e: E) => EResult;
}

class Result<TSuccess, TFailure> {
	public static Success<T>(success: T) {
		return new Result<T, any>(success, null, false);
	}

	public static Failure<T>(error: T) {
		return new Result<any, T>(null, error, true);
	}

	public static When =
		<T, E, TResult, TFailure>(mapFns: MapFns<T, E, TResult, TFailure>) =>
		(result: Result<T, E>) => {
			return result.when(mapFns);
		};

	constructor(
		private success: TSuccess,
		private failure: TFailure,
		private isError: boolean,
	) {}

	public when<T, E>(mapFns: MapFns<TSuccess, TFailure, T, E>) {
		return this.isError
			? mapFns.failure(this.failure)
			: mapFns.success(this.success);
	}

	public isOk(): boolean {
		return !this.failure && !this.isError;
	}

	public getSuccess(): TSuccess {
		return this.success;
	}

	public getFailure(): TFailure {
		return this.failure;
	}
}

export default Result;

// MRTM
export type BooleanResult = Result<boolean, ErrorResponse>;
export type StringResult = Result<string, ErrorResponse>;
export type NumberResult = Result<number, ErrorResponse>;
export type ArrayProductResult = Result<Array<Product>, ErrorResponse>;
export type PlanSummaryResult = Result<PlanSummary, ErrorResponse>;
export type CampaignResult = Result<Campaign, ErrorResponse>;
export type AuthResult = Result<AuthResponse, ErrorResponse>;
export type OTPTokenExpiryResult = Result<
	{ tokenExpiry: Moment },
	ErrorResponse
>;
export type MarketingPreferencesResult = Result<
	MarketingPreferences,
	ErrorResponse
>;

// PSMA
export type ArrayAddressSuggestionResult = Result<
	Array<AddressSuggestion>,
	ErrorResponse
>;
export type AddressResult = Result<Address, ErrorResponse>;

// Contentful
export type ArrayWelcomeCarouselSlideResult = Result<
	Array<WelcomeCarouselSlide>,
	ErrorResponse
>;
export type LegalContentResult = Result<LegalContent, ErrorResponse>;
export type AssetResult = Result<Asset, ErrorResponse>;
